export type ExecutionRowAction = 'OPEN' | 'SCALE_IN' | 'SCALE_OUT' | 'CLOSE';

export type ExecutionRowInput = {
  id?: string | number;
  timestamp?: number | string | Date;
  openTimestampMs?: number | string | Date;
  time?: number | string | Date;
  executedAt?: number | string | Date;
  date?: number | string | Date;
  side?: string;
  action?: string;
  execSide?: string;
  type?: string;
  volume?: number | string;
  qty?: number | string;
  quantity?: number | string;
  shares?: number | string;
  price?: number | string;
  avgPrice?: number | string;
  fillPrice?: number | string;
  entryPrice?: number | string;
  exitPrice?: number | string;
  entry_price?: number | string;
  avg_price?: number | string;
  commission?: number | string;
  fees?: number | string;
  fee?: number | string;
  commissionAndFees?: number | string;
  commissionsAndFees?: number | string;
};

export type ExecutionRowMatch = {
  openTimestampMs: number | null;
  closeTimestampMs: number;
  qty: number;
  openPrice: number;
  closePrice: number;
  realizedGross: number;
};

export type ResolvedExecutionRow = {
  index: number;
  timestampMs: number | null;
  side: 'buy' | 'sell';
  signedQty: number;
  quantity: number;
  price: number | null;
  rowAction: ExecutionRowAction;
  runningPosition: number;
  rowResolvedQty: number;
  rowCloseTimestampMs: number | null;
  rowDurationMinutes?: number;
  rowRealizedGross: number;
  rowFees: number;
  rowRealizedNet: number;
  matches: ExecutionRowMatch[];
};

type OpenLot = {
  rowIndex: number;
  openTimestampMs: number | null;
  price: number;
  qty: number;
  feePerUnit: number;
};

function toNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function toTimestampMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? Math.floor(value) : Math.floor(value * 1000);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const asNum = Number(value);
    if (Number.isFinite(asNum)) {
      return asNum > 1_000_000_000_000 ? Math.floor(asNum) : Math.floor(asNum * 1000);
    }

    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isFinite(ts) ? ts : null;
  }

  return null;
}

function normalizeSide(input: ExecutionRowInput): 'buy' | 'sell' {
  const raw = String(input.side ?? input.execSide ?? input.action ?? input.type ?? '').trim().toLowerCase();
  if (raw.includes('sell') || raw.includes('short') || raw === 's') return 'sell';
  if (raw.includes('buy') || raw.includes('long') || raw === 'b') return 'buy';

  const qty = Number(input.volume ?? input.qty ?? input.quantity ?? input.shares);
  if (Number.isFinite(qty) && qty !== 0) return qty < 0 ? 'sell' : 'buy';

  return 'buy';
}

function normalizeQty(input: ExecutionRowInput): number {
  const qty = Number(input.volume ?? input.qty ?? input.quantity ?? input.shares);
  if (!Number.isFinite(qty) || qty === 0) return 0;
  return Math.abs(qty);
}

function normalizePrice(input: ExecutionRowInput): number | null {
  return toNumber(
    input.price
    ?? input.avgPrice
    ?? input.fillPrice
    ?? input.entryPrice
    ?? input.exitPrice
    ?? input.entry_price
    ?? input.avg_price
  );
}

function normalizeCosts(input: ExecutionRowInput): number {
  const explicit = toNumber(input.commissionAndFees ?? input.commissionsAndFees);
  if (explicit != null) return explicit;
  return (toNumber(input.commission) ?? 0) + (toNumber(input.fees ?? input.fee) ?? 0);
}

export function resolveExecutionRows(
  executions: ExecutionRowInput[],
  options?: { multiplier?: number }
): ResolvedExecutionRow[] {
  const multiplier = Number.isFinite(Number(options?.multiplier)) ? Number(options?.multiplier) : 1;

  const normalized = executions.map((execution, index) => {
    const timestampMs = toTimestampMs(
      execution.openTimestampMs
      ?? execution.timestamp
      ?? execution.time
      ?? execution.executedAt
      ?? execution.date
    );
    const side = normalizeSide(execution);
    const quantity = normalizeQty(execution);
    const signedQty = quantity === 0 ? 0 : (side === 'buy' ? quantity : -quantity);
    const price = normalizePrice(execution);

    return {
      execution,
      index,
      timestampMs,
      side,
      quantity,
      signedQty,
      price,
      costs: normalizeCosts(execution),
    };
  });

  const resolved: ResolvedExecutionRow[] = normalized.map((row) => ({
    index: row.index,
    timestampMs: row.timestampMs,
    side: row.side,
    signedQty: row.signedQty,
    quantity: row.quantity,
    price: row.price,
    rowAction: 'OPEN',
    runningPosition: 0,
    rowResolvedQty: 0,
    rowCloseTimestampMs: null,
    rowDurationMinutes: undefined,
    rowRealizedGross: 0,
    rowFees: 0,
    rowRealizedNet: 0,
    matches: [],
  }));

  let runningPosition = 0;
  for (const row of normalized) {
    const prevPosition = runningPosition;
    runningPosition += row.signedQty;

    let rowAction: ExecutionRowAction = 'OPEN';
    if (prevPosition === 0 && runningPosition !== 0) {
      rowAction = 'OPEN';
    } else if (runningPosition === 0) {
      rowAction = 'CLOSE';
    } else if (Math.sign(prevPosition) !== 0 && Math.sign(runningPosition) !== 0 && Math.sign(prevPosition) !== Math.sign(runningPosition)) {
      rowAction = 'OPEN';
    } else if (Math.sign(row.signedQty) === Math.sign(prevPosition)) {
      rowAction = 'SCALE_IN';
    } else {
      rowAction = 'SCALE_OUT';
    }

    resolved[row.index].rowAction = rowAction;
    resolved[row.index].runningPosition = runningPosition;
  }

  const openLongLots: OpenLot[] = [];
  const openShortLots: OpenLot[] = [];

  const applyMatch = (
    closingRowIndex: number,
    closingFeePerUnit: number,
    lot: OpenLot,
    closePrice: number,
    closeTimestampMs: number | null,
    qty: number,
    grossPerUnit: number
  ) => {
    const gross = grossPerUnit * qty * multiplier;
    const feeAllocation = (lot.feePerUnit + closingFeePerUnit) * qty;
    const target = resolved[closingRowIndex];
    target.rowResolvedQty += qty;
    target.rowRealizedGross += gross;
    target.rowFees += feeAllocation;
    target.rowRealizedNet += gross - feeAllocation;
    if (closeTimestampMs != null) {
      target.rowCloseTimestampMs = closeTimestampMs;
    }
    target.matches.push({
      openTimestampMs: lot.openTimestampMs,
      closeTimestampMs: closeTimestampMs ?? 0,
      qty,
      openPrice: lot.price,
      closePrice,
      realizedGross: gross,
    });
  };

  for (const row of normalized) {
    let remainingQty = row.quantity;
    if (!remainingQty || row.price == null || !Number.isFinite(row.price)) continue;
    const closingFeePerUnit = row.quantity > 0 ? row.costs / row.quantity : 0;

    if (row.side === 'sell') {
      while (remainingQty > 0 && openLongLots.length > 0) {
        const lot = openLongLots[0];
        const matchedQty = Math.min(remainingQty, lot.qty);
        applyMatch(row.index, closingFeePerUnit, lot, row.price, row.timestampMs, matchedQty, row.price - lot.price);
        lot.qty -= matchedQty;
        remainingQty -= matchedQty;
        if (lot.qty <= 0) openLongLots.shift();
      }

      if (remainingQty > 0) {
        openShortLots.push({
          rowIndex: row.index,
          openTimestampMs: row.timestampMs,
          price: row.price,
          qty: remainingQty,
          feePerUnit: closingFeePerUnit,
        });
      }
      continue;
    }

    while (remainingQty > 0 && openShortLots.length > 0) {
      const lot = openShortLots[0];
      const matchedQty = Math.min(remainingQty, lot.qty);
      applyMatch(row.index, closingFeePerUnit, lot, row.price, row.timestampMs, matchedQty, lot.price - row.price);
      lot.qty -= matchedQty;
      remainingQty -= matchedQty;
      if (lot.qty <= 0) openShortLots.shift();
    }

    if (remainingQty > 0) {
      openLongLots.push({
        rowIndex: row.index,
        openTimestampMs: row.timestampMs,
        price: row.price,
        qty: remainingQty,
        feePerUnit: closingFeePerUnit,
      });
    }
  }

  for (const row of resolved) {
    if (row.rowResolvedQty > 0) {
      let weightedDurationMs = 0;
      let totalMatchedQty = 0;
      for (const match of row.matches) {
        if (!Number.isFinite(match.closeTimestampMs) || match.closeTimestampMs <= 0) continue;
        if (!Number.isFinite(Number(match.openTimestampMs))) continue;
        const durationMs = Math.max(0, match.closeTimestampMs - Number(match.openTimestampMs));
        weightedDurationMs += durationMs * match.qty;
        totalMatchedQty += match.qty;
      }
      if (totalMatchedQty > 0) {
        row.rowDurationMinutes = Math.max(0, Math.round((weightedDurationMs / totalMatchedQty) / 60000));
      }
    }
  }

  return resolved;
}